import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Rocket,
  Loader2,
  PauseCircle,
  PlayCircle,
  ShieldCheck,
  Image as ImageIcon,
  Save,
  Sparkles,
  Settings2,
  Clock4,
  ListChecks,
  Zap,
  Activity as ActivityIcon,
  CalendarRange,
  Wand2,
  Layers,
  ChevronRight,
  Send,
  Target,
  FileText,
  Circle,
  CheckCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/Surface";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import AIStatusOrb from "@/components/AIStatusOrb";
import ActivityFeed from "@/components/ActivityFeed";
import DayTimePicker from "@/components/DayTimePicker";
import SchedulePreviewCard from "@/components/SchedulePreviewCard";
import PlanUsageWidget from "@/components/PlanUsageWidget";
import BrandLearningStatusCard from "@/components/BrandLearningStatusCard";
import AILoadingState from "@/components/AILoadingState";
import AutomationEngineCard from "@/components/AutomationEngineCard";
import AutopilotStrategyCard from "@/components/AutopilotStrategyCard";
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Linkedin as LinkedinIcon,
  Lock,
  AlertTriangle,
  CheckCircle2,
  Plug,
} from "lucide-react";

// Canonical platform labels match what the backend stores on posts.
// ``connKey`` is the backend canonical key returned by /api/connections
// (e.g. "X" not "X / Twitter") so the connected/coming_soon lookup works.
// TikTok intentionally omitted from MVP launch (no video creation yet).
const AUTOPILOT_PLATFORM_OPTIONS = [
  { id: "X / Twitter", label: "X / Twitter", Icon: TwitterIcon, connKey: "X" },
  { id: "Instagram", label: "Instagram", Icon: InstagramIcon, connKey: "Instagram" },
  { id: "Facebook", label: "Facebook", Icon: FacebookIcon, connKey: "Facebook" },
  { id: "LinkedIn", label: "LinkedIn", Icon: LinkedinIcon, connKey: "LinkedIn" },
];

function ModeCard({ active, title, desc, icon: Icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        "text-left rounded-[16px] p-5 border transition-all w-full glass " +
        (active
          ? "glow-primary border-[color:rgba(27,168,159,0.55)]"
          : "border-[color:var(--border-subtle)] hover:border-[color:var(--border-strong)]")
      }
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
          <Icon className="h-4 w-4" />
        </div>
        <div className="font-display text-lg font-semibold">{title}</div>
        {active ? (
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">
            Active
          </span>
        ) : null}
      </div>
      <div className="text-sm text-[color:var(--text-2)]">{desc}</div>
    </button>
  );
}

function ConfidenceBar({ score = 92 }) {
  const v = Math.max(0, Math.min(100, score));
  return (
    <div className="space-y-1.5" data-testid="ai-confidence-bar">
      <div className="flex items-center justify-between text-xs">
        <span className="eyebrow">AI confidence</span>
        <span className="font-mono-tabular text-[color:var(--text-1)]">{v}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[color:var(--surface-3)] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: v + "%" }}
          transition={{ duration: 0.8, type: "spring", stiffness: 80, damping: 18 }}
          className="h-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)]"
        />
      </div>
    </div>
  );
}

function PostPreviewMini({ post }) {
  return (
    <div className="rounded-[14px] border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="pending-post-card">
      {post.image_url ? (
        <img
          src={post.image_url}
          alt="ai-generated"
          className="w-full aspect-square object-cover rounded-md mb-2 border border-[color:var(--border-subtle)]"
          data-testid="pending-post-image"
        />
      ) : (
        <div className="w-full aspect-square rounded-md mb-2 border border-[color:var(--border-subtle)] bg-gradient-to-br from-[color:var(--surface-3)] to-[color:var(--surface-2)] flex items-center justify-center text-[color:var(--text-3)]">
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
      <div className="flex items-center justify-between mb-1">
        <StatusBadge status={post.status} />
        <span className="text-[10px] text-[color:var(--text-3)]">
          <Clock4 className="h-3 w-3 inline mr-0.5" />
          {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : "—"}
        </span>
      </div>
      <div className="text-sm text-[color:var(--text-1)] whitespace-pre-line line-clamp-5">{post.caption}</div>
      {post.hashtags?.length ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {post.hashtags.slice(0, 8).map((h, i) => (
            <span key={i} className="text-[10px] rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)] px-1.5 py-0.5">{h}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AutopilotPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [singleRunning, setSingleRunning] = useState(false);
  const [pending, setPending] = useState([]);
  const [activity, setActivity] = useState([]);
  const [scheduleKey, setScheduleKey] = useState(0);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchCount, setBatchCount] = useState(3);

  const [planSub, setPlanSub] = useState(null);
  const [connections, setConnections] = useState([]);
  // Phase 17 platform-slot ctx — from same /connections call.
  const [slotCtx, setSlotCtx] = useState(null);
  const [libraryCount, setLibraryCount] = useState(null);
  // P0 Autopilot UI Overhaul: bump this to force AutomationEngineCard
  // to re-fetch /autopilot/status after Pause/Resume/manual generation.
  const [engineRefreshKey, setEngineRefreshKey] = useState(0);
  const bumpEngine = () => setEngineRefreshKey((k) => k + 1);

  const load = async () => {
    try {
      const [s, p, a, c, ml] = await Promise.all([
        api.get("/autopilot/settings"),
        api.get("/posts/pending-approval"),
        api.get("/stats/activity"),
        api.get("/connections").catch(() => null),
        api.get("/media-library").catch(() => null),
      ]);
      setCfg(s.data);
      setPending(p.data || []);
      setActivity(a.data?.events || []);
      setBatchCount(Math.max(1, s.data?.posts_per_week || 3));
      setConnections(c?.data?.platforms || []);
      setSlotCtx(c?.data?.platform_slots || null);
      setLibraryCount(ml?.data?.count ?? 0);
    } catch (_e) {
      toast.error("Could not load autopilot settings");
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Always treat the Autopilot screen as Autopilot mode — the previous
  // "Manual / Autopilot" mode chooser was removed because manual posting
  // is already its own first-class section (/generate). Cycle the flag if
  // we somehow land here in legacy "manual" mode.
  useEffect(() => {
    if (cfg && cfg.mode !== "autopilot") {
      setCfg((prev) => (prev ? { ...prev, mode: "autopilot" } : prev));
      api.put("/autopilot/settings", { mode: "autopilot" }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.mode]);

  const isAutopilot = true;
  const automationOn = !!(isAutopilot && cfg?.enabled && !cfg?.paused);
  const state = automationOn ? "running" : cfg?.paused ? "paused" : "idle";
  const completeness = useMemo(() => {
    if (!cfg) return 0;
    return [cfg.services, cfg.target_audience, cfg.promotional_goals].filter((x) => (x || "").trim().length > 8).length;
  }, [cfg]);
  const confidence = Math.min(98, 36 + completeness * 18 + (cfg?.posting_days?.length || 0) * 3 + (cfg?.posting_times?.length || 0) * 3);

  if (!cfg) {
    return <div className="text-sm text-[color:var(--text-3)]">Loading autopilot…</div>;
  }

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const onSave = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        mode: cfg.mode,
        enabled: cfg.enabled,
        approve_before_posting: cfg.approve_before_posting,
        services: cfg.services,
        target_audience: cfg.target_audience,
        promotional_goals: cfg.promotional_goals,
        posting_days: cfg.posting_days || ["Mon", "Wed", "Fri"],
        posting_times: cfg.posting_times || ["10:00"],
        // Capture the browser's IANA timezone (e.g. "America/New_York")
        // so the server interprets posting_times as user-local wall-clock
        // — 9:00 AM stays 9:00 AM local across DST. If the browser can't
        // determine one (extremely rare), the server falls back to UTC.
        timezone: cfg.timezone
          || (() => {
            try {
              return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            } catch (_e) {
              return "UTC";
            }
          })(),
        generate_images: cfg.generate_images,
        image_source: cfg.image_source || "ai",
        // Rotation fallback preference. Only meaningful when
        // image_source === "library". null/missing preserves today's
        // behavior on the server (resolves to "reuse", never spends AI
        // credits). Explicit values: "ai" | "reuse" | "text".
        library_fallback: cfg.library_fallback ?? null,
        target_platforms: cfg.target_platforms || [],
        // Owner-selected Autopilot content focus chips. Undefined → the
        // server treats it as "all four" (identical to legacy behavior).
        // We always send the current UI selection so a user can also
        // explicitly clear back to all four.
        content_focus: Array.isArray(cfg.content_focus) ? cfg.content_focus : ["promote", "educate", "brand_awareness", "community"],
        // Feb-2026 post-length preference. "short" was removed; legacy
        // values (including "short") coerce to "standard" so saved data
        // stays valid on the server.
        post_length: (["standard","long","detailed"]).includes(cfg.post_length) ? cfg.post_length : "standard",
        ...extra,
      };
      const res = await api.put("/autopilot/settings", payload);
      setCfg(res.data);
      setScheduleKey((k) => k + 1);
      toast.success("Autopilot settings saved");
    } catch (_e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const onRunSingle = async () => {
    setSingleRunning(true);
    try {
      await api.post("/autopilot/run-now");
      toast.success("Generated 1 post");
      await load();
      setScheduleKey((k) => k + 1);
      bumpEngine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Autopilot run failed");
    } finally {
      setSingleRunning(false);
    }
  };

  const onRunBatch = async () => {
    setBatchDialogOpen(false);
    setBatchRunning(true);
    try {
      const res = await api.post("/autopilot/run-batch", { count: Number(batchCount) || 3 });
      const n = res.data?.count ?? 0;
      const failures = res.data?.failures || [];
      const requested = res.data?.requested ?? n;
      if (n > 0 && failures.length > 0) {
        const reason = failures.find((f) => !f.soft)?.message || failures[0]?.message;
        toast.warning(
          `Generated ${n} of ${requested} posts. ${failures.length} failed${reason ? ` — ${reason}` : "."}`,
        );
      } else if (n > 0) {
        toast.success(`Generated ${n} post${n === 1 ? "" : "s"}`);
      } else {
        toast.error(failures[0]?.message || "Batch generation failed");
      }
      await load();
      setScheduleKey((k) => k + 1);
      bumpEngine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch generation failed");
    } finally {
      setBatchRunning(false);
    }
  };

  const onGenerateWeek = async () => {
    setBatchRunning(true);
    try {
      const res = await api.post("/autopilot/generate-week");
      const n = res.data?.count ?? 0;
      const failures = res.data?.failures || [];
      const requested = res.data?.requested ?? n;
      if (n > 0 && failures.length > 0) {
        const hardFailures = failures.filter((f) => !f.soft);
        const reason = (hardFailures[0] || failures[0])?.message;
        toast.warning(
          `Generated ${n} of ${requested} posts.${hardFailures.length ? ` ${hardFailures.length} failed — ${reason}` : ""}`,
        );
      } else if (n > 0) {
        toast.success(`Your week is ready — ${n} post${n === 1 ? "" : "s"} generated.`);
      } else {
        toast.error(
          failures[0]?.message || "No posts generated. Check your posting days/times.",
        );
      }
      await load();
      setScheduleKey((k) => k + 1);
      bumpEngine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Weekly generation failed");
    } finally {
      setBatchRunning(false);
    }
  };

  const onPauseToggle = async () => {
    try {
      const ep = cfg.paused ? "/autopilot/resume" : "/autopilot/pause";
      const res = await api.post(ep);
      setCfg(res.data);
      toast.success(cfg.paused ? "Automation resumed" : "Automation paused");
    } catch (_e) {
      toast.error("Could not update automation state");
    }
  };

  const approve = async (post) => {
    try {
      await api.post(`/posts/${post.post_id}/approve`);
      toast.success("Approved and scheduled");
      load();
      setScheduleKey((k) => k + 1);
    } catch (_e) {
      toast.error("Approve failed");
    }
  };
  const reject = async (post) => {
    try {
      await api.post(`/posts/${post.post_id}/reject`);
      toast.success("Rejected");
      load();
      setScheduleKey((k) => k + 1);
    } catch (_e) {
      toast.error("Reject failed");
    }
  };

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Autopilot"
          subtitle="Set it once. Postmoro fills the next 10 days automatically — no manual clicks needed."
          testidPrefix="autopilot"
        />

        {/* AUTOMATION ENGINE — the core, ambient AI that keeps the queue filled. */}
        <AutomationEngineCard
          cfg={cfg}
          onCfgRefresh={load}
          externalRefreshKey={engineRefreshKey}
        />

        {/* AUTOPILOT STRATEGY — owner tells Postmoro what to prioritize. */}
        <AutopilotStrategyCard cfg={cfg} onCfgRefresh={load} />

        {/* MANUAL TOOLS — clearly separated from the Automation Engine. */}
        <GlassCard className="p-5 mb-5" data-testid="autopilot-manual-tools">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-[240px] flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold flex items-center gap-2">
                <Layers className="h-3.5 w-3.5" />
                Manual Tools
              </div>
              <div className="font-display text-lg font-semibold mt-1 text-[color:var(--text-1)]">
                Need something extra right now?
              </div>
              <div
                className="text-xs text-[color:var(--text-3)] mt-1 max-w-[520px] leading-relaxed"
                data-testid="autopilot-manual-tools-helper"
              >
                Manual tools create extra content <em>without</em> changing your Autopilot schedule. Use them
                to top up your queue for a promo, a launch, or a slow week.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="autopilot-manual-tools-actions">
              <Button
                variant="outline"
                onClick={onRunSingle}
                disabled={singleRunning || !(cfg.target_platforms || []).length}
                title={!(cfg.target_platforms || []).length ? "Choose at least one platform first" : undefined}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="autopilot-run-now"
              >
                {singleRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Generate 1
              </Button>
              <Button
                onClick={onGenerateWeek}
                disabled={batchRunning || !(cfg.target_platforms || []).length}
                title={!(cfg.target_platforms || []).length ? "Choose at least one platform first" : undefined}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="autopilot-generate-week"
              >
                {batchRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                {batchRunning ? "Generating week…" : "Generate this week"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBatchDialogOpen(true)}
                disabled={batchRunning || !(cfg.target_platforms || []).length}
                className="text-xs text-[color:var(--text-3)] hover:text-[color:var(--text-1)] disabled:opacity-50"
                data-testid="autopilot-run-batch"
              >
                Custom batch…
              </Button>
            </div>
          </div>
        </GlassCard>


        {/* Polished AI loading state when generating a week */}
        {batchRunning ? (
          <div className="mb-5" data-testid="autopilot-week-loading-wrap">
            <AILoadingState variant="week" testidPrefix="autopilot-week-loading" />
          </div>
        ) : null}

        {/* Brand Learning quick status */}
        <div className="mb-4">
          <BrandLearningStatusCard testid="autopilot-brand-learning-status" compact />
        </div>

        {/* Plan + monthly usage awareness banner */}
        <div className="mb-4">
          <PlanUsageWidget compact onLoaded={setPlanSub} />
          {(() => {
            if (!planSub) return null;
            const limit = planSub.monthly_limit;
            const used = planSub.monthly_used || 0;
            const isUnlimited = limit === null || limit === undefined;
            const days = (cfg.posting_days || []).length;
            const times = (cfg.posting_times || []).length;
            // 4.33 weeks per month rounded down — same heuristic Autopilot uses.
            const projectedMonthly = days * times * 4;
            if (isUnlimited) {
              if (cfg.mode === "autopilot" && days > 0 && times > 0) {
                return (
                  <div
                    className="mt-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-[11px] text-[color:var(--text-2)]"
                    data-testid="autopilot-plan-projection-unlimited"
                  >
                    Business plan · Autopilot will generate about <strong className="text-[color:var(--text-1)]">{projectedMonthly} posts/month</strong> at your current schedule. Unlimited published posts (fair-use) — go wild.
                  </div>
                );
              }
              return null;
            }
            const remaining = Math.max(0, limit - used);
            const overBy = projectedMonthly - limit;
            const usagePct = Math.round((used / Math.max(1, limit)) * 100);
            // Hard-stopped — already at or past the cap
            if (used >= limit) {
              return (
                <div
                  className="mt-2 rounded-md border border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)] p-3 text-xs text-[color:var(--danger)] flex items-start gap-2"
                  data-testid="autopilot-plan-blocked"
                >
                  <span className="font-semibold">Monthly limit reached.</span>
                  <span className="text-[color:var(--text-2)] leading-relaxed">
                    You've used all {limit} published posts on the {planSub.effective_plan === "free" ? "Free Trial" : planSub.effective_plan} plan this month. Autopilot is paused for new generations until your next renewal or upgrade.
                    {" "}<Link to="/billing" className="text-[color:var(--accent-warm)] underline">Upgrade in Billing →</Link>
                  </span>
                </div>
              );
            }
            // Near-limit soft warning (>=80% used but not yet at cap)
            if (cfg.enabled && !cfg.paused && usagePct >= 80) {
              return (
                <div
                  className="mt-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3 text-xs text-[color:var(--accent-warm)] flex items-start gap-2"
                  data-testid="autopilot-plan-near-limit"
                >
                  <span className="font-semibold">Getting close to your monthly limit.</span>
                  <span className="text-[color:var(--text-2)] leading-relaxed">
                    You&apos;ve used {used} of {limit} posts this month. Autopilot may reach your monthly limit before renewal.
                    {" "}<Link to="/billing" className="text-[color:var(--accent-warm)] underline">Upgrade in Billing →</Link>
                  </span>
                </div>
              );
            }
            // Schedule exceeds plan
            if (cfg.mode === "autopilot" && projectedMonthly > limit && days > 0 && times > 0) {
              return (
                <div
                  className="mt-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3 text-xs text-[color:var(--accent-warm)] flex items-start gap-2"
                  data-testid="autopilot-plan-warning"
                >
                  <span className="leading-relaxed">
                    Your <strong className="capitalize">{planSub.effective_plan}</strong> plan includes <strong>{limit} published posts/month</strong>. Your current Autopilot schedule may create about <strong>{projectedMonthly} posts/month</strong>{overBy > 0 ? <> — that's {overBy} over your publish limit.</> : "."} Reduce frequency or{" "}<Link to="/billing" className="text-[color:var(--text-1)] underline">upgrade your plan</Link>.
                  </span>
                </div>
              );
            }
            // Healthy projection
            if (cfg.mode === "autopilot" && days > 0 && times > 0) {
              return (
                <div
                  className="mt-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-[11px] text-[color:var(--text-2)]"
                  data-testid="autopilot-plan-projection"
                >
                  <span className="capitalize">{planSub.effective_plan}</span> plan · {remaining}/{limit} published posts left this month · projected ~{projectedMonthly} from current schedule.
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Auto-set mode to autopilot the first time we land here, then
            jump straight to the settings + brief. Manual posting lives on
            the Generate Post page; surfacing a "Manual Mode" choice here
            confuses customers. */}

        {/* Settings + schedule preview + pending */}
        <div className="mb-4 text-[11px] text-[color:var(--text-3)] flex items-center gap-2">
          <span className="uppercase tracking-[0.16em]">Manual posting?</span>
          <Link
            to="/generate"
            className="text-[color:var(--brand-primary)] hover:underline"
            data-testid="autopilot-go-generate"
          >
            Open Generate Post →
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Business Brief — full width, top of grid */}
          <GlassCard className="p-6 lg:col-span-12 lg:order-1" delay={0.1} data-testid="autopilot-settings-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(34,211,238,0.16)] flex items-center justify-center border border-[color:rgba(27,168,159,0.35)]">
                <Zap className="h-4 w-4 text-[color:var(--brand-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">1 · Business Basics</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">What Postmoro writes about. Edit your details anytime — no need to re-enter them here.</div>
              </div>
            </div>
            {/* Concise read-only summary so users aren't re-filling a
                questionnaire. The editable fields live under Advanced. */}
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3 text-xs" data-testid="autopilot-business-summary">
              {[
                { l: "What you offer", v: cfg.services },
                { l: "Who you serve", v: cfg.target_audience },
                { l: "Goals", v: cfg.promotional_goals },
              ].map((it) => (
                <div key={it.l} className="min-w-[150px] max-w-[300px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] mb-0.5">{it.l}</div>
                  <div className="text-[color:var(--text-1)] leading-snug line-clamp-2">
                    {(it.v || "").trim() || <span className="text-[color:var(--text-3)] italic">Not set yet</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Link
                to="/settings/business-profile"
                className="text-xs text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1"
                data-testid="autopilot-edit-business-profile"
              >
                Edit Business Profile →
              </Link>
            </div>
            <details className="mt-4 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="autopilot-business-details">
              <summary className="cursor-pointer text-[11px] uppercase tracking-[0.14em] text-[color:var(--text-3)] hover:text-[color:var(--text-1)] select-none">
                Advanced · fine-tune what Autopilot uses
              </summary>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-3">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Services / offerings</label>
                  <Textarea
                    value={cfg.services || ""}
                    onChange={(e) => set({ services: e.target.value })}
                    placeholder="e.g. hand-tossed pizza, salads, craft beer, weekend brunch"
                    data-testid="autopilot-services"
                    className="mt-1 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] min-h-[92px]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Target audience</label>
                  <Textarea
                    value={cfg.target_audience || ""}
                    onChange={(e) => set({ target_audience: e.target.value })}
                    placeholder="e.g. families and UT Austin students, ages 18-45"
                    data-testid="autopilot-target-audience"
                    className="mt-1 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] min-h-[92px]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Promotional goals</label>
                  <Textarea
                    value={cfg.promotional_goals || ""}
                    onChange={(e) => set({ promotional_goals: e.target.value })}
                    placeholder="e.g. fill seats Tue–Thu nights, push weekend brunch, grow followers"
                    data-testid="autopilot-goals"
                    className="mt-1 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] min-h-[92px]"
                  />
                </div>
              </div>
            </details>
          </GlassCard>

          {/* Content Strategy Control Center — col-5, visually right of Schedule */}
          <GlassCard className="p-6 lg:col-span-5 lg:order-3" delay={0.14} data-testid="autopilot-strategy-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(47,143,229,0.18)] flex items-center justify-center border border-[color:rgba(27,168,159,0.40)]">
                <Target className="h-4 w-4 text-[color:var(--brand-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">2 · Content Direction</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">The essentials. Detailed goals, tone &amp; visuals live in the Strategy panel above.</div>
              </div>
            </div>
            {/* ---------- Content Focus chips ---------- */}
            {/* Owner-facing steering for Autopilot's existing content
                rotation. Selecting a subset weights the mapped categories
                higher inside the same picker — unselected categories
                stay eligible, and last-5 avoidance / social-proof gating
                / semantic-similarity guard / hook rotation are unchanged.
                All-four selected preserves today's rotation exactly. */}
            <details className="mt-5 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="autopilot-content-focus-section">
              <summary className="cursor-pointer text-xs uppercase tracking-[0.16em] text-[color:var(--text-3)] hover:text-[color:var(--text-1)] flex items-center gap-1.5 select-none">
                <Layers className="h-3 w-3" /> Advanced · content focus
              </summary>
              <p className="text-xs text-[color:var(--text-3)] mt-2 mb-2 leading-relaxed">
                Pick what Autopilot should lean into. Selecting all four keeps today&apos;s balanced mix.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="autopilot-content-focus-grid">
                {[
                  { id: "promote",         label: "Promote products & services", desc: "Highlights, offers, stronger CTAs" },
                  { id: "educate",         label: "Educate & share tips",         desc: "Quick tips and FAQs" },
                  { id: "brand_awareness", label: "Build brand awareness",         desc: "Behind the scenes, problem→solution" },
                  { id: "community",       label: "Engage local community",        desc: "Local angles, seasonal moments" },
                ].map((chip) => {
                  const active =
                    Array.isArray(cfg.content_focus)
                      ? cfg.content_focus.includes(chip.id)
                      : true; // legacy configs behave as all four selected
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(cfg.content_focus)
                          ? cfg.content_focus.slice()
                          : ["promote", "educate", "brand_awareness", "community"];
                        let next;
                        if (current.includes(chip.id)) {
                          next = current.filter((c) => c !== chip.id);
                          if (next.length === 0) {
                            // Never allow zero-selected: silently restore
                            // all four so the picker's fast path runs.
                            next = ["promote", "educate", "brand_awareness", "community"];
                            toast.info("Autopilot keeps at least one focus selected — restored all four.");
                          }
                        } else {
                          next = [...current, chip.id];
                        }
                        set({ content_focus: next });
                      }}
                      data-testid={`autopilot-content-focus-${chip.id}`}
                      aria-pressed={active}
                      className={
                        "rounded-md border p-3 text-left text-xs transition " +
                        (active
                          ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                      }
                    >
                      <div className="font-medium">{chip.label}</div>
                      <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 leading-snug">
                        {chip.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </details>

            {/* ---------- Post length ---------- */}
            {/* Feb-2026 UX polish: 3 equal-width premium segments
                (Short was removed; legacy "short" is coerced to
                "standard" both here and in _get_or_create_autopilot).
                Matches the Generate Post control 1:1. */}
            <div className="mt-5" data-testid="autopilot-post-length-section">
              <div className="text-xs uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-2">
                Post length
              </div>
              <p className="text-xs text-[color:var(--text-3)] mb-2 leading-relaxed">
                Choose how detailed Autopilot captions should usually be.
              </p>
              <div
                className="grid grid-cols-3 gap-2"
                role="radiogroup"
                aria-label="Autopilot post length"
                data-testid="autopilot-post-length"
              >
                {[
                  { id: "standard", label: "Standard", hint: "Quick, natural social post" },
                  { id: "long", label: "Long", hint: "More context and depth" },
                  { id: "detailed", label: "Detailed", hint: "Long-form social content" },
                ].map((opt) => {
                  const stored = (cfg.post_length || "standard");
                  // Legacy "short" (or anything unexpected) resolves to
                  // Standard so exactly one segment is always active.
                  const current = ["standard","long","detailed"].includes(stored) ? stored : "standard";
                  const active = current === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`autopilot-post-length-${opt.id}`}
                      onClick={() => set({ post_length: opt.id })}
                      className={
                        "group flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors " +
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgba(27,168,159,0.55)] " +
                        (active
                          ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.12)] text-[color:var(--text-1)]"
                          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:border-[color:rgba(27,168,159,0.35)] hover:bg-[color:rgba(27,168,159,0.06)] hover:text-[color:var(--text-1)]")
                      }
                    >
                      <span className={"text-sm font-medium leading-tight " + (active ? "text-[color:var(--text-1)]" : "")}>
                        {opt.label}
                      </span>
                      <span className="mt-0.5 text-[10px] leading-tight text-[color:var(--text-3)]">
                        {opt.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="autopilot-enabled-row">
                <span className="text-sm">Automation On/Off</span>
                <Switch
                  checked={!!cfg.enabled}
                  onCheckedChange={(v) => set({ enabled: v })}
                  data-testid="autopilot-enabled-switch"
                />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="autopilot-approve-row">
                <span className="text-sm">Approve before posting</span>
                <Switch
                  checked={!!cfg.approve_before_posting}
                  onCheckedChange={(v) => set({ approve_before_posting: v })}
                  data-testid="autopilot-approve-switch"
                />
              </label>
            </div>
          </GlassCard>

          {/* Platforms & Image Source — full width, comes after Strategy */}
          <GlassCard className="p-6 lg:col-span-12 lg:order-4" delay={0.16} data-testid="autopilot-platforms-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(34,211,238,0.24)] to-[color:rgba(27,168,159,0.16)] flex items-center justify-center border border-[color:rgba(34,211,238,0.35)]">
                <Plug className="h-4 w-4 text-[color:var(--brand-secondary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-secondary)]">3 · Distribution</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Where the posts go and how their images are sourced.</div>
              </div>
            </div>

            {/* ---------- Image source picker ---------- */}
            <div className="mt-5" data-testid="autopilot-image-source-section">
              <div className="text-xs uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-2 flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3" /> Image source
              </div>
              <div
                className="mb-2 rounded-md border border-[color:rgba(27,168,159,0.25)] bg-[color:rgba(27,168,159,0.05)] p-2.5 text-[11px] leading-snug"
                data-testid="autopilot-image-source-recommendation"
              >
                <span className="text-[color:var(--text-1)] font-medium">Your own photos usually perform best.</span>{" "}
                <span className="text-[color:var(--text-3)]">Let Autopilot pull from your Media Library for the most authentic posts. AI images are always available.</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  {
                    id: "library",
                    label: "Use Business Media",
                    desc: "Pick from your photos — most authentic",
                    recommended: true,
                  },
                  {
                    id: "ai",
                    label: "Generate with AI",
                    desc: "Counts toward AI image credits",
                  },
                  {
                    id: "text",
                    label: "Text-only posts",
                    desc: "No images attached",
                  },
                ].map((opt) => {
                  const active =
                    (cfg.image_source || (cfg.generate_images ? "ai" : "text")) === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set({ image_source: opt.id, generate_images: opt.id === "ai" })}
                      data-testid={`autopilot-image-source-${opt.id}`}
                      className={
                        "rounded-md border p-3 text-left text-xs transition " +
                        (active
                          ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                      }
                    >
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        {opt.label}
                        {opt.recommended ? (
                          <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[color:rgba(27,168,159,0.18)] text-[color:var(--brand-primary)] border border-[color:rgba(27,168,159,0.4)]">
                            Recommended
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 leading-snug">
                        {opt.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
              {(cfg.image_source || (cfg.generate_images ? "ai" : "text")) === "library" ? (
                <div className="mt-3 space-y-2" data-testid="autopilot-library-guidance">
                  {/* Approved primary heads-up — always visible while
                      "library" is selected. */}
                  <div
                    className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-xs text-[color:var(--text-2)]"
                    data-testid="autopilot-library-headsup"
                  >
                    Autopilot picks the best-matching image from your library for
                    each post&apos;s topic and rotates images when possible. With a
                    small library, photos may eventually be reused. Upload more
                    photos for greater variety.
                  </div>

                  {/* Size-aware advisory. Rendered when we know both the
                      current library count and the derived posts-per-week. */}
                  {libraryCount !== null && libraryCount >= 1 &&
                   Number.isFinite(Number(cfg.posts_per_week)) &&
                   Number(cfg.posts_per_week) > 0 ? (
                    <div
                      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-[11px] text-[color:var(--text-3)]"
                      data-testid="autopilot-library-size-note"
                    >
                      You currently have {libraryCount} image{libraryCount === 1 ? "" : "s"} and
                      schedule {Number(cfg.posts_per_week)} post{Number(cfg.posts_per_week) === 1 ? "" : "s"} per week. A larger library will
                      give Autopilot more visual variety.
                    </div>
                  ) : null}

                  {/* Empty-library special case — same CTA as before,
                      preserved verbatim. */}
                  {libraryCount === 0 ? (
                    <div
                      className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-2.5 text-xs"
                      data-testid="autopilot-library-empty-warning"
                    >
                      <div className="text-[color:var(--text-1)] font-medium">
                        Your Business Image Library is empty.
                      </div>
                      <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                        Autopilot will still generate captions, but posts won&apos;t have images
                        until you upload some.
                      </div>
                      <Link to="/media">
                        <Button
                          size="sm"
                          className="mt-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                          data-testid="autopilot-library-empty-cta"
                        >
                          Go to Business Image Library
                        </Button>
                      </Link>
                    </div>
                  ) : null}

                  {/* ────────────────────────────────────────────────
                      Rotation fallback selector. Rendered whenever
                      "library" is selected.

                      Legacy-user guard: cfg.library_fallback is null for
                      users who never explicitly saved a preference. In
                      that case we visually preselect "reuse" (matching
                      today's server-side behavior) so that hitting Save
                      without touching this control cannot cause a
                      surprise AI-image charge. New users who never had
                      a saved preference AND currently have an empty
                      library see "ai" preselected (matches the user's
                      requested default for first-time library users).
                     ──────────────────────────────────────────────── */}
                  {(() => {
                    const savedFallback = cfg.library_fallback;
                    const isLegacy = savedFallback == null;
                    // First-time-picking: show "ai" as the default
                    // choice so the user's preferred experience is
                    // pre-selected — but the credit note is right
                    // beside it so the choice is informed.
                    // Legacy user with a non-empty library: preserve
                    // their historic behavior by showing "reuse" as
                    // the visual default.
                    const visualDefault = isLegacy && libraryCount && libraryCount > 0
                      ? "reuse"
                      : (savedFallback || "ai");
                    const activeFallback = savedFallback || visualDefault;
                    return (
                      <div
                        className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                        data-testid="autopilot-library-fallback-section"
                      >
                        <div className="text-xs text-[color:var(--text-2)] mb-2 font-medium">
                          If the library is empty or the only viable image would
                          repeat, Autopilot should:
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {[
                            {
                              id: "ai",
                              label: "Use AI-generated images",
                              desc: "AI-generated fallback images use your AI image credits.",
                            },
                            {
                              id: "reuse",
                              label: "Reuse uploaded images",
                              desc: "Same image may repeat when needed. No AI credits used.",
                            },
                            {
                              id: "text",
                              label: "Create text-only posts",
                              desc: "Skip the image. No AI credits used.",
                            },
                          ].map((opt) => {
                            const active = activeFallback === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => set({ library_fallback: opt.id })}
                                data-testid={`autopilot-library-fallback-${opt.id}`}
                                className={
                                  "rounded-md border p-2.5 text-left text-xs transition " +
                                  (active
                                    ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                                    : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                                }
                              >
                                <div className="font-medium">{opt.label}</div>
                                <div
                                  className={
                                    "text-[10px] mt-0.5 leading-snug " +
                                    (opt.id === "ai"
                                      ? "text-[color:var(--warning)]"
                                      : "text-[color:var(--text-3)]")
                                  }
                                >
                                  {opt.desc}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {isLegacy ? (
                          <div
                            className="mt-2 text-[10px] text-[color:var(--text-3)]"
                            data-testid="autopilot-library-fallback-legacy-hint"
                          >
                            Your current setting is <em>Reuse</em>. Save your Autopilot
                            settings to lock in a different choice.
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </div>

            {/* ---------- Platform multi-select ---------- */}
            <div className="mt-5" data-testid="autopilot-platforms-section">
              <div className="text-xs uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-2 flex items-center gap-1.5">
                <Plug className="h-3 w-3" /> Autopilot platforms
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AUTOPILOT_PLATFORM_OPTIONS.map((opt) => {
                  const conn = connections.find(
                    (c) => c.platform === opt.connKey || c.name === opt.connKey,
                  );
                  const comingSoon = !!conn?.coming_soon;
                  const awaitingReview = !!conn?.awaiting_app_review;
                  const connected = !!conn?.connected;
                  const selected = (cfg.target_platforms || []).includes(opt.id);
                  // Phase 17 platform-slot gating. connKey is one of
                  // "Facebook" | "Instagram" | "X" | "LinkedIn"; slotCtx
                  // uses lowercase canonicals ("x" not "X / Twitter").
                  const canon = (opt.connKey || "").toLowerCase();
                  const slotInactive =
                    !!slotCtx &&
                    !slotCtx.unlimited &&
                    !(slotCtx.active_platforms || []).includes(canon);
                  const disabled = comingSoon || awaitingReview || slotInactive;
                  const Icon = opt.Icon;
                  const lockTooltip = comingSoon
                    ? (opt.connKey === "LinkedIn" ? "Planned — arriving after launch" : "Coming soon")
                    : slotInactive
                    ? `Not active on your ${(slotCtx?.plan || "").replace(/^./, (c) => c.toUpperCase())} plan — Manage platforms`
                    : awaitingReview
                    ? "Requires Meta approval before live posting is available"
                    : undefined;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        const cur = new Set(cfg.target_platforms || []);
                        if (cur.has(opt.id)) cur.delete(opt.id);
                        else cur.add(opt.id);
                        set({ target_platforms: Array.from(cur) });
                      }}
                      title={lockTooltip}
                      data-testid={`autopilot-platform-${opt.id.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={
                        "relative flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition " +
                        (selected
                          ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                          : disabled
                          ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed opacity-60"
                          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:border-[color:var(--border-strong)]")
                      }
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1 truncate">{opt.label}</span>
                      {selected ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
                      ) : disabled ? (
                        <Lock className="h-3.5 w-3.5 text-[color:var(--text-3)]" />
                      ) : !connected ? (
                        <span
                          className="text-[10px] uppercase tracking-wider text-[color:var(--accent-warm)]"
                          title="Not connected — connect in Settings → Connections"
                        >
                          Not connected
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              {!(cfg.target_platforms || []).length ? (
                slotCtx && !slotCtx.unlimited && (slotCtx.active_platforms || []).length === 0 ? (
                  <div
                    className="mt-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    data-testid="autopilot-choose-platforms-empty"
                  >
                    <div>
                      <div className="text-sm font-semibold text-[color:var(--text-1)]">
                        Choose where Autopilot should post
                      </div>
                      <div className="text-xs text-[color:var(--text-3)] mt-0.5 leading-relaxed">
                        Connect and select up to {slotCtx.platform_limit} platform
                        {slotCtx.platform_limit === 1 ? "" : "s"} included with your{" "}
                        {(slotCtx.plan || "").replace(/^./, (c) => c.toUpperCase())} plan.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.location.assign("/settings/connections")}
                      className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold shrink-0"
                      data-testid="autopilot-choose-platforms-cta"
                    >
                      Choose platforms
                    </Button>
                  </div>
                ) : (
                  <div
                    className="mt-2 flex items-start gap-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-2.5 text-xs text-[color:var(--text-2)]"
                    data-testid="autopilot-no-platform-warning"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[color:var(--accent-warm)] shrink-0" />
                    Choose at least one connected platform before Autopilot can schedule posts.
                  </div>
                )
              ) : null}
              {(cfg.target_platforms || []).some((p) => {
                if (!slotCtx || slotCtx.unlimited) return false;
                const canon = (p || "").toLowerCase();
                return !(slotCtx.active_platforms || []).includes(canon);
              }) ? (
                <div
                  className="mt-2 flex items-start gap-2 rounded-md border border-[color:rgba(255,138,61,0.55)] bg-[color:rgba(255,138,61,0.08)] p-2.5 text-xs text-[color:var(--text-1)]"
                  data-testid="autopilot-inactive-platform-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[color:var(--accent-warm)] shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium">Needs attention</div>
                    <div>
                      One or more platforms in this Autopilot are not active on your
                      current plan and won&apos;t run. Update your selection or
                      <button
                        type="button"
                        onClick={() => window.location.assign("/settings/connections")}
                        className="ml-1 text-[color:var(--brand-primary)] hover:underline"
                        data-testid="autopilot-manage-platforms-link"
                      >
                        manage platforms
                      </button>
                      .
                    </div>
                  </div>
                </div>
              ) : null}
              {(cfg.target_platforms || []).some((p) => {
                const c = connections.find((cc) => cc.platform === p || cc.name === p);
                return c && !c.connected && !c.coming_soon;
              }) ? (
                <div
                  className="mt-2 flex items-start gap-2 rounded-md border border-[color:rgba(255,138,61,0.30)] bg-[color:rgba(255,138,61,0.04)] p-2.5 text-xs text-[color:var(--text-2)]"
                  data-testid="autopilot-not-connected-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[color:var(--accent-warm)] shrink-0" />
                  Some selected platforms aren&apos;t connected yet. Autopilot will skip them until you connect them in{" "}
                  <Link to="/settings/connections" className="underline text-[color:var(--text-1)] ml-0.5">Settings → Connections</Link>.
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={() => onSave()}
                disabled={saving}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="autopilot-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save settings
              </Button>
            </div>
          </GlassCard>

          {/* Schedule — Weekly Rhythm 7-day planner */}
          <GlassCard className="p-6 lg:col-span-7 lg:order-2" delay={0.12} data-testid="autopilot-schedule-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(34,211,238,0.24)] to-[color:rgba(27,168,159,0.16)] flex items-center justify-center border border-[color:rgba(34,211,238,0.35)]">
                <CalendarRange className="h-4 w-4 text-[color:var(--brand-secondary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-secondary)]">3 · Schedule</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Pick the days and times that fit your customers&apos; scroll.</div>
              </div>
            </div>

            {/* 7-day visual planner — real posting_days + posting_times */}
            {(() => {
              const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
              const dayLong = { Sun:"Sunday",Mon:"Monday",Tue:"Tuesday",Wed:"Wednesday",Thu:"Thursday",Fri:"Friday",Sat:"Saturday" };
              const activeDays = new Set(cfg.posting_days || []);
              const times = cfg.posting_times || [];
              // Compute the DAY of the next scheduled run so we can highlight
              // it. cfg.next_run_at is a real backend value — no invention.
              let nextDay = null;
              if (cfg.next_run_at) {
                try {
                  const dt = new Date(cfg.next_run_at);
                  nextDay = DAYS[dt.getDay()];
                } catch (_e) { /* ignore */ }
              }
              return (
                <div className="mb-4" data-testid="autopilot-week-planner">
                  <div className="grid grid-cols-7 gap-1.5">
                    {DAYS.map((d) => {
                      const on = activeDays.has(d);
                      const isNext = nextDay === d;
                      return (
                        <div
                          key={d}
                          className={
                            "rounded-lg border p-2 min-h-[104px] flex flex-col transition-colors " +
                            (on
                              ? isNext
                                ? "border-[color:rgba(27,168,159,0.75)] bg-[color:rgba(27,168,159,0.16)] shadow-[0_0_0_1px_rgba(27,168,159,0.35),0_8px_20px_-12px_rgba(27,168,159,0.5)]"
                                : "border-[color:rgba(27,168,159,0.45)] bg-[color:rgba(27,168,159,0.08)]"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] opacity-70")
                          }
                          data-testid={`autopilot-week-planner-${d.toLowerCase()}`}
                          title={dayLong[d] + (isNext ? " · next run" : "")}
                        >
                          <div className={
                            "text-[10px] uppercase tracking-wider text-center font-semibold " +
                            (on ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-3)]")
                          }>
                            {d}
                          </div>
                          <div className="mt-1.5 flex-1 flex flex-col gap-1">
                            {on ? (
                              times.length ? times.map((t, i) => (
                                <div
                                  key={i}
                                  className={
                                    "text-[10px] font-mono-tabular text-center rounded-md py-1 border " +
                                    (isNext && i === 0
                                      ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.20)] text-[color:var(--brand-primary)]"
                                      : "border-[color:rgba(27,168,159,0.30)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--brand-primary)]")
                                  }
                                >
                                  {t}
                                </div>
                              )) : (
                                <div className="text-[9px] text-center text-[color:var(--text-3)] italic">no times</div>
                              )
                            ) : (
                              <div className="text-[9px] text-center text-[color:var(--text-3)]">Off</div>
                            )}
                          </div>
                          {isNext ? (
                            <div className="mt-1 text-[9px] uppercase tracking-wider text-center text-[color:var(--brand-primary)] font-semibold">
                              Next run
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] flex items-center gap-3 flex-wrap">
                    <span>{activeDays.size} day{activeDays.size === 1 ? "" : "s"}</span>
                    <span className="opacity-40">·</span>
                    <span>{times.length} slot{times.length === 1 ? "" : "s"}</span>
                    {cfg.next_run_at ? (
                      <>
                        <span className="opacity-40">·</span>
                        <span>Next run: <span className="text-[color:var(--text-1)] font-medium normal-case tracking-normal">{new Date(cfg.next_run_at).toLocaleString([], { weekday:"short", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}</span></span>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })()}

            <div className="hairline my-4" />
            <div className="font-display text-base font-semibold mb-2">Edit days &amp; times</div>
            <DayTimePicker
              days={cfg.posting_days || []}
              times={cfg.posting_times || []}
              onChangeDays={(days) => set({ posting_days: days })}
              onChangeTimes={(times) => set({ posting_times: times })}
            />
            <div className="hairline my-4" />
            <div className="font-display text-base font-semibold mb-2">Upcoming schedule</div>
            <SchedulePreviewCard refreshKey={scheduleKey} weeks={2} />
          </GlassCard>

          {/* Pending approval */}
          <GlassCard className="p-6 lg:col-span-12 lg:order-5" delay={0.18} data-testid="autopilot-pending-card">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(255,190,110,0.20)] to-[color:rgba(27,168,159,0.14)] flex items-center justify-center border border-[color:rgba(255,190,110,0.35)]">
                  <ShieldCheck className="h-4 w-4 text-[color:var(--accent-warm)]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--accent-warm)]">Review Queue</div>
                  <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Approve or reject drafts before they publish.</div>
                </div>
              </div>
              <span className="text-xs text-[color:var(--text-3)] inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {pending.length}</span>
            </div>
            {pending.length === 0 ? (
              <div
                className="relative overflow-hidden rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-6 py-10 text-center"
                data-testid="autopilot-pending-empty"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{ background: "radial-gradient(ellipse at top, rgba(27,168,159,0.10), transparent 65%)" }}
                />
                <div className="relative">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[color:rgba(27,168,159,0.10)] border border-[color:rgba(27,168,159,0.35)] flex items-center justify-center mb-3">
                    <ShieldCheck className="h-5 w-5 text-[color:var(--brand-primary)]" />
                  </div>
                  <div className="text-sm font-medium text-[color:var(--text-1)]">All caught up</div>
                  <div className="text-xs text-[color:var(--text-3)] mt-1 max-w-sm mx-auto leading-relaxed">
                    No posts awaiting approval. Click <strong className="text-[color:var(--text-1)]">Generate this week</strong> to draft a full week at once.
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pending.map((p) => (
                  <motion.div
                    key={p.post_id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <PostPreviewMini post={p} />
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                        onClick={() => approve(p)}
                        data-testid={`approve-post-${p.post_id}`}
                      >
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]"
                        onClick={() => reject(p)}
                        data-testid={`reject-post-${p.post_id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </GlassCard>

          {/* Activity */}
          <GlassCard className="p-6 lg:col-span-12 lg:order-6" delay={0.20} data-testid="autopilot-activity-card">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(47,143,229,0.18)] flex items-center justify-center border border-[color:rgba(27,168,159,0.40)]">
                  <ActivityIcon className="h-4 w-4 text-[color:var(--brand-primary)]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Live Feed</div>
                  <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Every generation, schedule, and publish event.</div>
                </div>
              </div>
              <span className="pulse-dot" aria-hidden="true" />
            </div>
            <div className="max-h-[420px] overflow-auto pr-1">
              <ActivityFeed events={activity} emptyText="No activity yet — flip Automation On or click Generate this week to see the AI move." />
            </div>
          </GlassCard>
        </div>

        <div className="mt-6 text-xs text-[color:var(--text-3)]">
          Tip: you can still create one-off posts from{" "}
          <Link to="/generate" className="text-[color:var(--brand-primary)] hover:underline">Generate Posts</Link>, and edit anything in the{" "}
          <Link to="/calendar" className="text-[color:var(--brand-primary)] hover:underline">Content Calendar</Link>.
        </div>
      </div>

      {/* Batch run dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--brand-primary)]" /> Generate this week's posts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[color:var(--text-2)]">
              We'll create <span className="text-[color:var(--text-1)] font-semibold">{batchCount}</span> post{batchCount === 1 ? "" : "s"} and spread them across your selected days/times.
            </div>
            <div>
              <label className="text-xs text-[color:var(--text-2)]">How many posts?</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[1, 3, 5, 7, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setBatchCount(n)}
                    data-testid={`batch-count-${n}`}
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-medium border transition-all " +
                      (batchCount === n
                        ? "chip-grad"
                        : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                    }
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            {cfg.generate_images ? (
              <div className="text-xs text-[color:var(--accent-warm)]">⏱ AI image generation takes ~20s per post. Larger batches may take a few minutes.</div>
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setBatchDialogOpen(false)} data-testid="batch-cancel">Cancel</Button>
            <Button
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
              onClick={onRunBatch}
              disabled={batchRunning}
              data-testid="batch-confirm"
            >
              {batchRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
              Generate {batchCount}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
